import { calculateItemWeight } from './itemHelpers'

export enum ItemValidationErrors {
  ChoiceNone = 1, // Item has no choices.
  ChoiceMin, // A minimum of 2 choices allowed. Item has ${choiceLength} choice.
  ChoiceMax, // A maximum of 9 choices allowed. Item has ${choiceLength} choices.
  CorrectNone, // Item requires correct answer.'
  Rubric1PTExcludeZeroNotAllowed, // 'Item with 1PT max rubric cannot exclude zero.
  RubricWeightZeroNotAllowed, // Weight cannot be zero
  RubricWeightMismatch, // Question weight does not match item weight.
  PartialScoringWeightZeroNotAllowed, // Partial scoring is not supported with item weight of 0'
  WeightMin, // Item type must have a weight of 1 or higher.
}

/**
 * Validate item model
 * @param itemType ibx systemKey
 * @param config PIE item config
 * @param weight Question weight to validate against
 * @returns any[]
 */
export const validate = ({
  itemType,
  config,
  weight,
  partialScoring,
}: {
  itemType: string
  config: any
  weight?: number
  partialScoring?: boolean
}): any[] => {
  let errors = []

  switch (itemType) {
    case 'multiple_choice':
    case 'multiple_select':
      errors = [...validateMultipleChoice(config), ...errors]
      break
    case 'constructed_response':
    case 'drawing_response':
      errors = [...validateRubrics(config, weight), ...errors]
      break
    // may be released at a later date
    // case 'explicit_constructed_response':
    //   errors = [...validateWeightMin(weight), ...errors]
    //   break
  }

  return [...errors, ...validatePartialScoring(weight, partialScoring)]
}

/**
 * Validate Multiple Choice Model
 * Checks for: correct choice, max/min choice options
 * @param config PIE item config
 * @returns any[]
 */
export const validateMultipleChoice = (config: any): any[] => {
  const errors = []
  const model = config.models?.[0] || { choices: [] }
  const choiceLength = model.choices?.length || 0
  const correct = model.choices.reduce((m, choice) => {
    if (choice.correct) {
      m = choice.correct
    }
    return m
  }, null)

  // validate has choices
  if (choiceLength == 0) {
    errors.push(ItemValidationErrors.ChoiceNone)
  }

  // validate has less than one choice
  if (choiceLength == 1) {
    errors.push(ItemValidationErrors.ChoiceMin)
  }

  // validate choices >= allowed max
  if (choiceLength > 9) {
    errors.push(ItemValidationErrors.ChoiceMax)
  }

  // validate has correct choice
  if (correct !== true) {
    errors.push(ItemValidationErrors.CorrectNone)
  }

  return errors
}

/**
 * Validate Rubric Models
 * @param config PIE item config
 * @param weight Question weight to validate against
 * @returns any[]
 */
export const validateRubrics = (config: any, weight?: number): any[] => {
  let errors = []

  // model validation
  config?.models?.forEach((model) => {
    // model calculation
    let excludeZero = null
    let maxPoints = calculateItemWeight(config)

    if (model?.points?.length) {
      excludeZero = model?.excludeZero
    } else if (model?.rubricType) {
      if (model.rubricType === 'simpleRubric') {
        excludeZero = model?.rubrics?.simpleRubric?.excludeZero
      } else if (model.rubricType === 'multiTraitRubric') {
        excludeZero = model?.rubrics?.multiTraitRubric?.excludeZero
      }
    } else if (model?.rubrics?.simpleRubric) {
      excludeZero = model?.rubrics?.simpleRubric?.excludeZero
    } else if (model?.rubrics?.multiTraitRubric) {
      excludeZero = model?.rubrics?.multiTraitRubric?.excludeZero
    }

    // model checks
    if (excludeZero && maxPoints === 1) {
      errors.push(ItemValidationErrors.Rubric1PTExcludeZeroNotAllowed)
    }
  })

  // weight validation
  if (typeof weight === 'number') {
    errors = [...errors, ...validateRubricQuestionWeight(config, weight)]
  }

  return errors
}

/**
 * Validate Rubric Weights
 * @param config PIE item config
 * @param weight Question weight to validate against
 * @returns any[]
 */
export const validateRubricQuestionWeight = (config: any, weight: number): any[] => {
  const errors = []

  const calculatedWeight = calculateItemWeight(config)
  if (weight === 0 || calculatedWeight === 0) {
    errors.push(ItemValidationErrors.RubricWeightZeroNotAllowed)
  }

  if (weight !== calculatedWeight) {
    errors.push(ItemValidationErrors.RubricWeightMismatch)
  }

  return errors
}

/**
 * Validate partial scoring
 * @param weight Question weight to validate against
 * @param partialScoring Question partial scoring
 */
export const validatePartialScoring = (weight: number, partialScoring: boolean): any[] => {
  const errors = []

  if (weight === 0 && partialScoring) {
    errors.push(ItemValidationErrors.PartialScoringWeightZeroNotAllowed)
  }

  return errors
}

/**
 * Validate weight min
 * @param weight Question weight to validate against
 
 */
export const validateWeightMin = (weight: number): any[] => {
  const errors = []

  if (weight === 0) {
    errors.push(ItemValidationErrors.WeightMin)
  }

  return errors
}
